import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  COLOR_SELECT_CHANNEL_MAX,
  COLOR_SELECT_CUSTOM_LABEL,
  COLOR_SELECT_ESCAPE_KEY,
  COLOR_SELECT_EYEDROPPER_LABEL,
  COLOR_SELECT_FALLBACK_COLOR,
  COLOR_SELECT_FORMAT_TOGGLE_LABEL,
  COLOR_SELECT_HEX_FIELD_LABEL,
  COLOR_SELECT_HEX_LABEL,
  COLOR_SELECT_HUE_MAX,
  COLOR_SELECT_PALETTE,
  COLOR_SELECT_POSITIONS,
  COLOR_SELECT_RESET_LABEL,
  COLOR_SELECT_RGB_LABELS,
} from './color-select.constants';
import {
  ColorValueFormat,
  EyeDropperConstructor,
  HsvColor,
  RgbColor,
} from './color-select.types';
import {
  clampFraction,
  hexToHsv,
  hexToRgb,
  hsvToHex,
  hueSpectrumGradient,
  normalizeHexColor,
  rgbToHex,
} from './color-select.utils';

/** A color swatch that opens a picker panel; clicking the swatch again closes it. */
@Component({
  selector: 'firebird-web-color-select',
  templateUrl: './color-select.component.html',
  styleUrls: ['./color-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ColorSelectComponent {
  @Input() public value: string | null | undefined = '';
  @Input() public defaultValue = '';
  @Input() public disabled = false;
  @Input() public compact = false;
  @Input() public tooltip = '';
  @Input() public resetTooltip = 'Reset to default color';
  @Input() public showPalette = true;
  @Input() public label = COLOR_SELECT_CUSTOM_LABEL;

  // Emits null on reset so callers can drop the override instead of storing the default.
  @Output() public valueChange = new EventEmitter<string | null>();

  @ViewChild('origin', { read: ElementRef })
  public origin?: ElementRef<HTMLElement>;

  public isOpen = false;
  public hsv: HsvColor = hexToHsv(COLOR_SELECT_FALLBACK_COLOR);
  public hexText = '';
  public valueFormat: ColorValueFormat = 'rgb';

  public readonly palette = COLOR_SELECT_PALETTE;
  public readonly positions = COLOR_SELECT_POSITIONS;
  public readonly resetLabel = COLOR_SELECT_RESET_LABEL;
  public readonly hexLabel = COLOR_SELECT_HEX_LABEL;
  public readonly hexFieldLabel = COLOR_SELECT_HEX_FIELD_LABEL;
  public readonly rgbLabels = COLOR_SELECT_RGB_LABELS;
  public readonly channelMax = COLOR_SELECT_CHANNEL_MAX;
  public readonly eyedropperLabel = COLOR_SELECT_EYEDROPPER_LABEL;
  public readonly formatToggleLabel = COLOR_SELECT_FORMAT_TOGGLE_LABEL;
  public readonly hueMax = COLOR_SELECT_HUE_MAX;
  public readonly hueTrack = hueSpectrumGradient();
  // Only Chromium browsers have the EyeDropper API.
  public readonly canPickFromScreen = !!this.eyeDropper;

  private isDragging = false;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  /** Pure hue behind the saturation/brightness area. */
  public get hueColor(): string {
    return hsvToHex({ h: this.hsv.h, s: 1, v: 1 });
  }

  public get currentColor(): string {
    return hsvToHex(this.hsv);
  }

  public get rgb(): RgbColor {
    return hexToRgb(this.currentColor);
  }

  public get isCustom(): boolean {
    return (
      !!this.defaultValue &&
      (this.value ?? '').toLowerCase() !== this.defaultValue.toLowerCase()
    );
  }

  private get eyeDropper(): EyeDropperConstructor | undefined {
    return (globalThis as unknown as { EyeDropper?: EyeDropperConstructor })
      .EyeDropper;
  }

  public isSelected(color: string): boolean {
    return (this.value ?? '').toLowerCase() === color.toLowerCase();
  }

  public toggle(event: MouseEvent): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen && !this.disabled;
    if (this.isOpen) {
      this.syncFromValue();
    }
  }

  public close(): void {
    this.isOpen = false;
  }

  // The swatch toggles itself, so a click on it must not also close the panel here.
  public onOutsideClick(event: MouseEvent): void {
    if (this.origin?.nativeElement.contains(event.target as Node)) {
      return;
    }
    this.close();
  }

  public onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === COLOR_SELECT_ESCAPE_KEY) {
      event.stopPropagation();
      this.close();
    }
  }

  public onAreaPointerDown(event: PointerEvent): void {
    this.isDragging = true;
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    this.pickFromArea(event);
  }

  public onAreaPointerMove(event: PointerEvent): void {
    if (this.isDragging) {
      this.pickFromArea(event);
    }
  }

  // Dragging only previews; the color is emitted once on release.
  public onAreaPointerUp(): void {
    if (!this.isDragging) {
      return;
    }
    this.isDragging = false;
    this.emitCurrent();
  }

  public onHueInput(event: Event): void {
    this.applyHsv({
      ...this.hsv,
      h: (event.target as HTMLInputElement).valueAsNumber,
    });
  }

  public onHueChange(): void {
    this.emitCurrent();
  }

  // The field is rewritten directly: an unchanged binding would leave invalid text on screen.
  public onHexChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const hex = normalizeHexColor(input.value);
    if (hex) {
      this.select(hex);
    }
    input.value = this.hexText;
  }

  // Blurring commits through (change), so Enter doesn't emit a second time.
  public onHexEnter(event: Event): void {
    (event.target as HTMLInputElement).blur();
  }

  public onRgbChange(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const channel = input.valueAsNumber;
    if (!Number.isNaN(channel)) {
      const rgb = [...this.rgb] as RgbColor;
      rgb[index] = channel;
      this.select(rgbToHex(rgb));
    }
    input.value = String(this.rgb[index]);
  }

  public toggleFormat(): void {
    this.valueFormat = this.valueFormat === 'rgb' ? 'hex' : 'rgb';
  }

  public pickFromScreen(): void {
    const EyeDropper = this.eyeDropper;
    if (!EyeDropper) {
      return;
    }
    new EyeDropper()
      .open()
      .then(({ sRGBHex }) => {
        this.select(sRGBHex);
        this.cdr.markForCheck();
      })
      // Escape cancels the pick; nothing to do.
      .catch(() => undefined);
  }

  public select(color: string): void {
    const hex = normalizeHexColor(color) ?? color;
    this.hsv = hexToHsv(hex);
    this.hexText = hex;
    this.valueChange.emit(hex);
  }

  public reset(event: MouseEvent): void {
    event.stopPropagation();
    this.valueChange.emit(null);
    // The parent hands back the default; show it right away.
    if (this.defaultValue) {
      this.hsv = hexToHsv(this.defaultValue);
      this.hexText = normalizeHexColor(this.defaultValue) ?? '';
    }
  }

  private syncFromValue(): void {
    const hex =
      normalizeHexColor(this.value) ??
      normalizeHexColor(this.defaultValue) ??
      COLOR_SELECT_FALLBACK_COLOR;
    this.hsv = hexToHsv(hex);
    this.hexText = hex;
  }

  private pickFromArea(event: PointerEvent): void {
    const area = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.applyHsv({
      h: this.hsv.h,
      s: clampFraction(event.clientX - area.left, area.width),
      v: 1 - clampFraction(event.clientY - area.top, area.height),
    });
  }

  private applyHsv(hsv: HsvColor): void {
    this.hsv = hsv;
    this.hexText = hsvToHex(hsv);
  }

  private emitCurrent(): void {
    this.valueChange.emit(this.hexText);
  }
}
